import type { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { TokenManager } from "../../utils/auth";
import type { AuthTokens, RequestConfig } from "./types";
import { ErrorHandler } from "./errorHandler";

export class AuthManager {
  private httpClient: AxiosInstance;
  private errorHandler: ErrorHandler;

  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
  }

  /**
   * Attach authentication token to request
   */
  attachToken(config: AxiosRequestConfig): AxiosRequestConfig {
    const token = TokenManager.getToken();

    if (token && TokenManager.isValidToken(token)) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle authentication failures.
   *
   * The Rust backend exposes short-lived JWT login/logout/me routes but no
   * refresh-token endpoint, so a 401 cannot be repaired client-side. Clear
   * local credentials and let the user re-authenticate instead of retrying a
   * stale /api/auth/refresh route.
   */
  async handleAuthFailure(
    error: AxiosError,
    originalRequest: AxiosRequestConfig & { _retry?: boolean },
  ): Promise<never> {
    if (error.response?.status !== 401 || originalRequest._retry) {
      throw error;
    }

    originalRequest._retry = true;
    this.handleAuthenticationFailure();
    throw this.errorHandler.handleError(error);
  }

  /**
   * Handle complete authentication failure
   */
  private handleAuthenticationFailure(): void {
    // Clear all authentication data
    TokenManager.clearAll();

    // Redirect to login page
    // In a real app, you might want to use a router or emit an event
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }

  /**
   * Check if request requires authentication
   */
  requiresAuth(config: AxiosRequestConfig): boolean {
    // Skip auth for certain endpoints or if explicitly disabled
    const skipAuth = (config as RequestConfig).skipAuth;
    if (skipAuth) {
      return false;
    }

    // Skip auth for login/register endpoints
    const url = config.url || "";
    const authSkipPatterns = [
      "/api/auth/google",
      "/api/jobs", // Public job listings
      "/api/health",
    ];

    return !authSkipPatterns.some((pattern) => url.includes(pattern));
  }

  /**
   * Get current authentication status
   */
  isAuthenticated(): boolean {
    const token = TokenManager.getToken();
    return token !== null && TokenManager.isValidToken(token);
  }

  /**
   * Get current user token
   */
  getToken(): string | null {
    return TokenManager.getToken();
  }

  /**
   * Set authentication tokens
   */
  setTokens(tokens: AuthTokens): void {
    TokenManager.setToken(tokens.accessToken);

    if (tokens.refreshToken) {
      TokenManager.setRefreshToken(tokens.refreshToken);
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    TokenManager.clearAll();
  }

  /**
   * Handle logout
   */
  async logout(): Promise<void> {
    try {
      // Attempt to notify server of logout
      await this.httpClient.post("/api/auth/logout");
    } catch (error) {
      // Continue with logout even if server request fails
      this.errorHandler.logError(
        this.errorHandler.handleError(error),
        "Logout server notification failed",
      );
    } finally {
      this.clearAuth();
    }
  }
}
